import { Button } from '@/components/ui';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useState } from 'react';
import { ReturnSignButton } from './ReturnSignButton';

/**
 * ReturnSignSection — P0-2 (FE-3 D4 capstone) finalize-előtti jogi-jegyzőkönyv + ügyfél-aláírás.
 *
 * Flow (Phase-1, Zeno-ratify — documentId-threading NÉLKÜL, a shared SignContractDialog érintetlen):
 *   1. jegyzőkönyv-generálás: POST /rentals/:id/return-doc a checklist `accessories[]` WRITE-tal
 *      (+ conditionNotes) → `pdfUrl` + jogi-jegyzőkönyv (returnDocumentId a rentalon),
 *   2. ügyfél-aláírás: `ReturnSignButton` (sourceType=RENTAL_RETURN, sourceId=rentalId) → `returnSignedAt`,
 *   3. `onSigned` → a hívó (RentalReturnPage) ezzel gate-eli a véglegesítést (aláírás-kötelező, jogi-P0).
 */
export function ReturnSignSection({
  rentalId,
  rentalNumber,
  accessories,
  conditionNotes,
  signed,
  onSigned,
}: {
  rentalId: string;
  rentalNumber?: string | undefined;
  accessories: { accessoryId: string; status: 'OK' | 'MISSING' | 'DAMAGED' }[];
  conditionNotes?: string | undefined;
  /** A jogi-P0 aláírás-státusz — single source a parentben (C1-fix), itt CSAK megjelenítjük. */
  signed: boolean;
  onSigned: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [docId, setDocId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  // C1-fix (jogi-P0): a return-tartalom változására a generált jegyzőkönyv elavul → doc-reset
  // (re-generate kötelező). A `signed` a parentből jön + ott is reset-elődik tartalom-változásra.
  useEffect(() => {
    setDocId(null);
  }, [accessories, conditionNotes]);

  const handleGenerate = async (): Promise<void> => {
    if (generating) return;
    setGenerating(true);
    try {
      const { createReturnDoc } = await import('@/api/return-doc');
      const res = await createReturnDoc(rentalId, {
        accessories,
        conditionNotes: conditionNotes || undefined,
      });
      setDocId(res.data.documentId);
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : t('rental.return.error.generic'),
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="space-y-3 rounded-lg border border-border p-4"
      data-testid="return-sign-section"
    >
      <h3 className="text-sm font-semibold">{t('rental.return.protocolTitle')}</h3>

      {!docId ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => void handleGenerate()}
          disabled={generating}
          data-testid="return-doc-generate-btn"
        >
          {generating ? t('common.processing') : t('rental.return.generateDoc')}
        </Button>
      ) : !signed ? (
        <ReturnSignButton
          rentalId={rentalId}
          rentalNumber={rentalNumber}
          onSigned={() => onSigned()}
        />
      ) : (
        <p
          className="text-sm font-medium text-green-700 dark:text-green-400"
          data-testid="return-signed-ok"
        >
          ✓ {t('rental.return.signedOk')}
        </p>
      )}
    </div>
  );
}
